import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";

const PCT_ROWS: string[][] = [
  ["Regional phase entry (filing act)", "€135", "€800", "Online entry, formalities, representative appointment"],
  ["Supplementary European search (where due)", "€1,595", "—", "Not due where the EPO was the ISA"],
  ["Designation fee", "€720", "—", "Covers all EPC states"],
  ["Examination fee (Euro-PCT)", "€2,240", "€450", "Due on entry"],
  ["Claims 16–50", "€290 each", "—", "US claim sets are usually trimmed before entry"],
  ["Rule 161/162 response", "—", "€300 – €1,500", "€300 formal; substantive amendment is priced per objection"],
];

const EPO_ROWS: string[][] = [
  ["Direct EP filing", "€135", "€800", "Alternative to the PCT route"],
  ["European search", "€1,595", "—", "Search report plus written opinion"],
  ["Designation fee", "€720", "—", "Paid with the examination request"],
  ["Examination fee (direct filing)", "€2,010", "€450", "Starts substantive examination"],
  ["Renewal fee, year 3", "€725", "€270", "Rises each year while pending"],
  ["Grant and printing", "€1,135", "€450", "Plus claim translations into French and German"],
];

const OFFICE_ACTION_ROWS: string[][] = [
  ["Clarity / indefiniteness", "€100 per objection"],
  ["Novelty", "€200 per cited embodiment"],
  ["Inventive step", "€300 per objection"],
  ["Eligibility (technical character)", "€200 per objection"],
  ["Unity of invention", "€250 per independent claim, per objection"],
  ["Claim amendments", "€150 per claim"],
  ["Formal corrections", "€250 per correction"],
];

const VALIDATION_ROWS: string[][] = [
  ["Germany", "None", "€350 – €700"],
  ["France", "None", "€300 – €600"],
  ["United Kingdom", "None", "€300 – €600"],
  ["Netherlands", "None (English accepted)", "€350 – €700"],
  ["Italy / Spain", "Full translation", "€1,200 – €2,500 each"],
  ["Unitary Patent (participating EU states)", "One translation during the transition", "€1,000 – €1,800"],
];

const FAQS = [
  {
    q: "What does a European patent cost for a US company in total?",
    a: "From filing to grant, official fees are roughly €5,600 to €7,000 and attorney fees €2,500 to €6,000, depending on the number of examination rounds. Validation or a Unitary Patent request adds €1,000 to €8,000, and renewals run for up to twenty years.",
  },
  {
    q: "Is the PCT route more expensive than direct EPO filing?",
    a: "Per step it is similar — the Euro-PCT examination fee is €2,240 against €2,010 for a direct filing — but the PCT route defers most European spend by up to eighteen months and can avoid a second search where the EPO acted as ISA.",
  },
  {
    q: "How much does an EPO office action response cost?",
    a: "Typically €1,500 to €3,500. Fees are built up per objection — for example two clarity objections, two novelty citations, one inventive-step objection and two claim amendments come to €1,200 in attorney fees.",
  },
  {
    q: "How much does validation cost after grant?",
    a: "€300 to €700 per country where no translation is required and €1,000 to €2,500 where a full translation is. A five-country validation usually lands between €3,000 and €8,000 in one-off costs.",
  },
];

export const Route = createFileRoute("/services/european-patent-costs-for-us-companies")({
  head: () => ({
    meta: [
      { title: "European Patent Costs for US Companies (PCT, EPO, Validation)" },
      {
        name: "description",
        content:
          "Official and attorney fees for US companies: Euro-PCT regional phase entry, direct EPO filing, office action responses and validation — with worked totals in EUR.",
      },
      { property: "og:title", content: "European Patent Costs for US Companies" },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "PCT entry, EPO prosecution, office action responses and validation costs in one place, priced in EUR for US applicants.",
      },
      {
        property: "og:url",
        content: "https://bauerip.com/services/european-patent-costs-for-us-companies",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://bauerip.com/services/european-patent-costs-for-us-companies",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: CostsPage,
});

function FeeTable({
  caption,
  head,
  rows,
}: {
  caption: string;
  head: string[];
  rows: string[][];
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-secondary/50 text-foreground">
          <tr>
            {head.map((h) => (
              <th key={h} scope="col" className="px-4 py-3 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-muted-foreground">
          {rows.map((row) => (
            <tr key={row[0]} className="border-t border-border">
              {row.map((cell, i) => (
                <td key={i} className={`px-4 py-3 ${i === 0 ? "text-foreground" : ""}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        <Section
          eyebrow="Costs for US applicants"
          title="European Patent Costs for US Companies: PCT, EPO, Office Actions and Validation"
          titleAs="h1"
          intro="Every European cost a US company meets, in one place and in euros: entering the regional phase from a PCT application, prosecuting at the EPO, answering an examination report, and validating or requesting unitary effect after grant. Official fees are the EPO's 2026 schedule; attorney fees are my indicative rates, confirmed as a fixed fee before work starts."
        >
          <div className="flex flex-wrap gap-3">
            <CTAButton
              variant="accent"
              trackLabel="costs_page_quote"
              href={mailto({
                subject: "European patent cost estimate",
                body: "Hello Karel,\n\nPlease send a fixed-fee estimate.\n\n— Route (PCT regional phase / direct EP filing):\n— Application or publication number:\n— Technology field:\n— Number of claims and independent claims:\n— Next deadline:\n",
              })}
            >
              Request a fixed-fee estimate
            </CTAButton>
            <Link
              to="/services/european-patent-attorney"
              className="inline-flex items-center justify-center rounded-full border border-foreground/25 px-6 py-3 text-sm font-medium tracking-wide text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
            >
              Work with a European Patent Attorney →
            </Link>
          </div>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Step 1 — PCT"
          title="Euro-PCT regional phase entry"
          intro="Most US companies reach Europe through the PCT. Entry is due 31 months from the earliest priority date and is non-extendable; the six-month Rule 161/162 window that follows is where the claim set is adapted to EPO practice."
        >
          <FeeTable
            caption="Euro-PCT regional phase entry fees"
            head={["Item", "Official fee", "Attorney fee", "Notes"]}
            rows={PCT_ROWS}
          />
          <p className="mt-6 text-sm text-muted-foreground">
            Typical entry with a 15-claim set and no supplementary search:{" "}
            <strong className="text-foreground">€3,095 official + €1,250 attorney</strong>. Detail:{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: "pct-regional-phase-europe-guide" }}
              className="underline underline-offset-4"
            >
              the 31-month PCT national phase guide
            </Link>
            .
          </p>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Step 2 — EPO"
          title="Direct EPO filing and prosecution"
          intro="A direct European filing suits a US company that wants an early European search or has no PCT application. From the search report onward the cost structure is identical to the Euro-PCT route."
        >
          <FeeTable
            caption="Direct EPO filing and prosecution fees"
            head={["Item", "Official fee", "Attorney fee", "Notes"]}
            rows={EPO_ROWS}
          />
          <p className="mt-6 text-sm text-muted-foreground">
            Renewal fees accrue every year the application is pending — see the{" "}
            <Link to="/tools/epo-renewal-fees" className="underline underline-offset-4">
              EPO renewal fee tables and calculator
            </Link>
            .
          </p>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Step 3 — Office actions"
          title="What an EPO office action response costs"
          intro="Article 94(3) examination reports are priced per objection rather than by an open hourly count, so you can budget the response before it is drafted."
        >
          <FeeTable
            caption="EPO office action response attorney fees"
            head={["Objection type", "Indicative attorney fee"]}
            rows={OFFICE_ACTION_ROWS}
          />
          <p className="mt-6 text-sm text-muted-foreground">
            Worked example: two clarity objections, two novelty citations, one inventive-step
            objection and amendments to two claims ={" "}
            <strong className="text-foreground">€1,200</strong>. A full response usually lands
            between €1,500 and €3,500. More:{" "}
            <Link to="/services/epo-office-action-response" className="underline underline-offset-4">
              EPO office action response service
            </Link>
            .
          </p>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Step 4 — After grant"
          title="Validation and Unitary Patent costs"
          intro="A granted European patent must be brought into force country by country, or turned into a Unitary Patent with a single renewal stream. The translation requirement, not the agent, drives the cost."
        >
          <FeeTable
            caption="Validation and Unitary Patent costs"
            head={["Territory", "Translation", "One-off cost"]}
            rows={VALIDATION_ROWS}
          />
          <p className="mt-6 text-sm text-muted-foreground">
            Country-by-country detail in the{" "}
            <Link
              to="/tools/european-patent-validation-costs"
              className="underline underline-offset-4"
            >
              validation cost tool
            </Link>{" "}
            ·{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: "unitary-patent-upc-us-companies-guide" }}
              className="underline underline-offset-4"
            >
              Unitary Patent and UPC for US companies
            </Link>
          </p>
        </Section>

        <Section className="border-t border-border" eyebrow="FAQ" title="European patent costs — common questions">
          <dl className="grid gap-8 md:grid-cols-2">
            {FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-serif text-xl text-foreground">{f.q}</dt>
                <dd className="mt-3 text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-12 rounded-2xl border border-border bg-secondary/30 p-6 md:p-8">
            <h2 className="font-serif text-xl text-foreground">Get your own numbers</h2>
            <p className="mt-3 max-w-2xl text-muted-foreground leading-relaxed">
              Send the application and the next deadline and you get a written fixed fee covering
              every step above, with official fees listed separately.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <CTAButton
                variant="accent"
                trackLabel="costs_page_footer_quote"
                href={mailto({ subject: "European patent cost estimate" })}
              >
                Request a fixed-fee estimate
              </CTAButton>
              <Link
                to="/patent-ai"
                className="inline-flex items-center justify-center rounded-full border border-foreground/25 px-6 py-3 text-sm font-medium tracking-wide text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
              >
                Estimate it now with Ask AI →
              </Link>
            </div>
          </div>
        </Section>
      </main>

      <SiteFooter />
    </div>
  );
}
